import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Fade,
  CircularProgress,
  TextField,
  Stack,
  Tooltip,
  LinearProgress,
  Divider,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { QRCodeSVG } from "qrcode.react";
import { AuthContext } from "../../context/AuthContext/AuthContext";
import {
  startMfaEnrollment,
  confirmMfaEnrollment,
} from "../../utils/services/control/mfaService";
import secureStorage from "../../utils/security/secureStorage";
import LoginBack from "../../assets/Images/LoginBack2.png";

/**
 * Pantalla-bloqueo para usuarios autenticados que todavía NO tienen 2FA activo.
 *
 * Requisito HIPAA (45 CFR 164.312(d) – Person or Entity Authentication):
 * todo usuario con acceso a PHI debe autenticarse con al menos dos factores.
 * Mientras el usuario tenga `mfaEnabled=false`, el ProtectedRoute lo manda
 * aquí y no puede ver el resto de la aplicación hasta que complete el
 * enrollment (QR → código TOTP → guardar 8 recovery codes).
 *
 * Flujo interno (3 pasos):
 *   "intro"  — explica por qué se requiere 2FA + botón "Set up now".
 *   "scan"   — QR + input TOTP de 6 dígitos.
 *   "codes"  — muestra los 8 recovery codes una sola vez + botón continuar.
 *
 * Al completar, persistimos mfaEnabled=true en AuthContext y navegamos a /Main.
 */
const MfaRequiredPage = () => {
  const navigate = useNavigate();
  const { user, setMfaEnabled, signOut } = useContext(AuthContext);

  const [step, setStep] = useState("intro");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [otpAuthUri, setOtpAuthUri] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);

  const [copyFeedback, setCopyFeedback] = useState(null);

  // Guards: si ya tiene MFA activo al aterrizar → fuera de aquí.
  // Si no hay usuario → login. Solo chequeamos en el paso "intro" para no
  // sacar al usuario cuando setMfaEnabled(true) se dispara tras el confirm
  // y todavía estamos mostrando los recovery codes (paso "codes").
  useEffect(() => {
    if (!user) {
      navigate("/Login", { replace: true });
      return;
    }
    if (step === "intro" && user.mfaEnabled === true) {
      navigate("/Main", { replace: true });
    }
  }, [user, navigate, step]);

  // ───────────────────── Enrollment handlers ─────────────────────

  const handleStartEnrollment = useCallback(async () => {
    setError(null);
    setLoading(true);
    setOtpAuthUri("");
    setTotpCode("");
    try {
      const data = await startMfaEnrollment();
      if (!data?.success || !data.otpAuthUri) {
        setError(
          data?.message ||
            "Could not start 2FA enrollment. Please try again."
        );
        return;
      }
      setOtpAuthUri(data.otpAuthUri);
      setStep("scan");
    } catch (err) {
      setError("Network error starting 2FA enrollment. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConfirmTotp = useCallback(async () => {
    setError(null);
    if (totpCode.length !== 6) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setLoading(true);
    try {
      const data = await confirmMfaEnrollment(totpCode);
      if (!data?.success || !Array.isArray(data.recoveryCodes)) {
        setError(data?.message || "Invalid code. Please try again.");
        setTotpCode("");
        return;
      }
      setRecoveryCodes(data.recoveryCodes);
      setMfaEnabled(true);
      secureStorage.removeItem("mfa_low_codes_warning");
      setStep("codes");
    } catch (err) {
      setError("Network error confirming the code. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [totpCode, setMfaEnabled]);

  const handleFinish = () => {
    if (!codesAcknowledged) return;
    navigate("/Main", { replace: true });
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/Login", { replace: true });
  };

  // ───────────────────── Recovery codes actions ─────────────────────

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopyFeedback("Codes copied to clipboard");
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch {
      setCopyFeedback("Copy failed — please select and copy manually");
      setTimeout(() => setCopyFeedback(null), 3000);
    }
  };

  const downloadCodes = () => {
    const content =
      "MCM Visual — 2FA Recovery Codes\n" +
      "================================\n\n" +
      recoveryCodes
        .map((c, i) => `${String(i + 1).padStart(2, "0")}. ${c}`)
        .join("\n") +
      "\n\n" +
      "Keep these codes in a safe place. They allow you to sign in if you\n" +
      "lose access to your authenticator app. Each code can only be used ONCE.\n" +
      `Generated: ${new Date().toLocaleString()}\n` +
      `User: ${user?.email || ""}\n`;
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mcm-2fa-recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printCodes = () => {
    const w = window.open("", "_blank", "width=600,height=700");
    if (!w) return;
    w.document.write(`
      <html>
        <head>
          <title>MCM 2FA Recovery Codes</title>
          <style>
            body { font-family: monospace; padding: 32px; }
            h2 { font-family: sans-serif; }
            .code { font-size: 18px; letter-spacing: 2px; margin: 8px 0; }
            .note { font-family: sans-serif; color: #555; font-size: 12px; margin-top: 24px; }
          </style>
        </head>
        <body>
          <h2>MCM Visual — 2FA Recovery Codes</h2>
          <p>User: ${user?.email || ""}</p>
          <p>Generated: ${new Date().toLocaleString()}</p>
          <hr />
          ${recoveryCodes
            .map((c, i) => `<div class="code">${i + 1}. ${c}</div>`)
            .join("")}
          <p class="note">
            Keep these codes in a safe place. Each code can only be used ONCE.
            They allow you to sign in if you lose access to your authenticator app.
          </p>
        </body>
      </html>
    `);
    w.document.close();
    w.focus();
    w.print();
  };

  // Durante el paso "codes" la bandera mfaEnabled YA es true localmente pero
  // necesitamos seguir renderizando la página para que el usuario guarde sus
  // recovery codes antes de continuar.
  if (!user) return null;
  if (user.mfaEnabled === true && step === "intro") return null;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundImage: `url(${LoginBack})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Fade in timeout={500}>
        <Card
          sx={{
            width: "100%",
            maxWidth: step === "codes" ? 560 : 480,
            borderRadius: 4,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
            bgcolor: "rgba(255, 255, 255, 0.97)",
            backdropFilter: "blur(10px)",
            overflow: "hidden",
          }}
        >
          {loading && <LinearProgress sx={{ height: 3 }} />}
          <CardContent sx={{ px: 4, py: 4 }}>
            {/* Header común */}
            <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "#0C343D",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShieldIcon sx={{ color: "#fff", fontSize: 36 }} />
              </Box>
              <Typography
                variant="h5"
                fontWeight={700}
                color="#0C343D"
                textAlign="center"
              >
                {step === "codes"
                  ? "Save Your Recovery Codes"
                  : "Two-Factor Authentication Required"}
              </Typography>
              {user?.email && step === "intro" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  Signed in as <strong>{user.email}</strong>
                </Typography>
              )}
            </Stack>

            {/* Paso 1: Intro con explicación HIPAA */}
            {step === "intro" && (
              <>
                <Alert
                  severity="warning"
                  icon={<LockIcon />}
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  Access to this application is blocked until you enroll in
                  two-factor authentication.
                </Alert>

                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
                  This system contains <strong>Protected Health
                  Information (PHI)</strong>. Under the{" "}
                  <strong>HIPAA Security Rule
                  (45 CFR §164.312(d) — Person or Entity Authentication)</strong>,
                  every user who accesses PHI must verify their identity using
                  at least two independent factors.
                </Typography>

                <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
                  To comply with this requirement — and to protect patient
                  data from unauthorized access — you must enroll a
                  time-based one-time password (TOTP) authenticator before
                  you can continue.
                </Typography>

                <Box
                  sx={{
                    bgcolor: "#f5f8fa",
                    border: "1px solid #e0e7ec",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="#0C343D"
                    sx={{ mb: 1 }}
                  >
                    What you&apos;ll need
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    An authenticator app installed on your phone:
                    Microsoft Authenticator, Google Authenticator, Authy,
                    1Password, Bitwarden, or any TOTP-compatible app.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    bgcolor: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="#0C343D"
                    sx={{ mb: 1 }}
                  >
                    What happens next
                  </Typography>
                  <Typography
                    variant="body2"
                    component="ol"
                    sx={{ m: 0, pl: 2.5, lineHeight: 1.8 }}
                  >
                    <li>Scan a QR code with your authenticator app.</li>
                    <li>
                      Enter the 6-digit code your app generates to confirm.
                    </li>
                    <li>
                      Save the 8 recovery codes we&apos;ll give you — they let
                      you sign in if you lose your phone.
                    </li>
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<ShieldIcon />}
                  onClick={handleStartEnrollment}
                  disabled={loading}
                  sx={{
                    bgcolor: "#0C343D",
                    py: 1.3,
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#0a2a33" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} sx={{ color: "#fff" }} />
                  ) : (
                    "Set Up Two-Factor Authentication"
                  )}
                </Button>

                <Divider sx={{ my: 2.5 }} />
                <Button
                  fullWidth
                  variant="text"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  sx={{ color: "text.secondary" }}
                >
                  Sign out
                </Button>
              </>
            )}

            {/* Paso 2: Scan QR + TOTP */}
            {step === "scan" && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Scan this QR code with your authenticator app, then enter
                  the 6-digit code it generates to finish the setup.
                </Typography>

                {otpAuthUri && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        p: 2,
                        bgcolor: "#fff",
                        border: "1px solid #eee",
                        borderRadius: 2,
                        mb: 2,
                      }}
                    >
                      <QRCodeSVG value={otpAuthUri} size={220} includeMargin />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Can&apos;t scan the QR? Enter this code manually:
                    </Typography>
                    <Box
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        bgcolor: "#f5f5f5",
                        p: 1,
                        borderRadius: 1,
                        mb: 2,
                        wordBreak: "break-all",
                      }}
                    >
                      {otpAuthUri.match(/secret=([A-Z2-7]+)/i)?.[1] ||
                        otpAuthUri}
                    </Box>

                    <TextField
                      autoFocus
                      fullWidth
                      label="Verification code"
                      value={totpCode}
                      onChange={(e) =>
                        setTotpCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6)
                        )
                      }
                      placeholder="000000"
                      inputProps={{
                        inputMode: "numeric",
                        maxLength: 6,
                        style: {
                          textAlign: "center",
                          fontSize: "1.5rem",
                          letterSpacing: "0.4rem",
                          fontFamily: "monospace",
                        },
                      }}
                      sx={{ mb: 2 }}
                    />
                  </>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Stack direction="row" spacing={1.5}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setStep("intro");
                      setError(null);
                      setTotpCode("");
                    }}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleConfirmTotp}
                    disabled={totpCode.length !== 6 || loading || !otpAuthUri}
                    sx={{
                      bgcolor: "#0C343D",
                      "&:hover": { bgcolor: "#0a2a33" },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : (
                      "Confirm & Activate"
                    )}
                  </Button>
                </Stack>
              </>
            )}

            {/* Paso 3: Save recovery codes */}
            {step === "codes" && (
              <>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{ mb: 2 }}
                >
                  These 8 codes will only be shown <strong>once</strong>.
                  Save them in a safe place (password manager, printed copy,
                  etc.). They let you sign in if you lose access to your
                  phone. Each code can only be used one time.
                </Alert>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                    p: 2,
                    bgcolor: "#f5f5f5",
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  {recoveryCodes.map((code, i) => (
                    <Typography
                      key={code}
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        color: "#0C343D",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}. {code}
                    </Typography>
                  ))}
                </Box>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Tooltip title="Copy all codes">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={copyCodes}
                    >
                      Copy
                    </Button>
                  </Tooltip>
                  <Tooltip title="Download as .txt">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={downloadCodes}
                    >
                      Download
                    </Button>
                  </Tooltip>
                  <Tooltip title="Print">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={printCodes}
                    >
                      Print
                    </Button>
                  </Tooltip>
                </Stack>

                {copyFeedback && (
                  <Alert
                    severity="success"
                    icon={<CheckCircleIcon />}
                    sx={{ mb: 2 }}
                  >
                    {copyFeedback}
                  </Alert>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <input
                    id="mfa-required-ack-checkbox"
                    type="checkbox"
                    checked={codesAcknowledged}
                    onChange={(e) => setCodesAcknowledged(e.target.checked)}
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                  />
                  <label
                    htmlFor="mfa-required-ack-checkbox"
                    style={{ cursor: "pointer" }}
                  >
                    <Typography variant="body2">
                      I have saved these codes in a safe place
                    </Typography>
                  </label>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleFinish}
                  disabled={!codesAcknowledged}
                  sx={{
                    bgcolor: "#0C343D",
                    py: 1.3,
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#0a2a33" },
                  }}
                >
                  I saved them — Continue to the app
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
};

export default MfaRequiredPage;
